// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import SubstituteProductsLink from "@steiner/App/Components/Product/SubstituteProductsLink";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import DataTable from "@steiner/MobiusOverrides/DataTable";
import Tooltip from "@steiner/MobiusOverrides/Tooltip";
import AlertSuccess from "@steiner/XDL/Icons/AlertSuccess";
import Slash from "@steiner/XDL/Icons/Slash";
import { parseBoolean } from "@steiner/XDL/Utilities";
import { xParserOptions } from "@steiner/XDL/Utilities/BasicSelectors";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Changes
    isNonStockPropertyItem?: boolean;
    qtyPerWarehouse?: string;
    qtyOnHand?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
    steinerAvailabilityDetailsUnauthenticated:
        getSettingsCollection(state).customSharedSettings.steinerAvailabilityDetailsUnauthenticated,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Changes
    nonStockTooltip?: LinkPresentationProps;
    notifyMessage?: InjectableCss;
    availabilityByWarehouseMessage?: TypographyPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

// XNG-Changes: change style + add injectCss
const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    width: 100%;
    gap: 4px;

    ${IconWrapper} {
        width: 12px;
        height: 12px;
        margin-top: 1px;

        svg {
            width: 12px;
            height: 12px;
        }
    }
    ${injectCss}
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

// xng-change
const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return Slash;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return AlertSuccess;
};

// XNG-Changes
// Add isAuthenticated and warehouses to the parameters and condition
// Steiner hides the link for non authenticated users
// And if inventory message is out of stock in all warehouses
const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    // XNG-Changes
    isAuthenticated?: boolean,
    warehouses?: [string, string][],
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse ||
        !isAuthenticated ||
        (availability.messageType === AvailabilityMessageType.OutOfStock &&
            (!warehouses?.length || warehouses?.every(w => !w[1])))
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Changes
    isAuthenticated,
    isNonStockPropertyItem,
    qtyPerWarehouse,
    qtyOnHand,
    steinerAvailabilityDetailsUnauthenticated,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // XNG-Changes
    // In Steiner they return the warehouses in inventory.additionalResults.["qtyPerWarehouse"]

    const [productInventoryWarehouses, setProductInventoryWarehouses] = useState<[string, string][] | undefined>(
        undefined,
    );

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    // XNG-Changes
    // Stop sending the getRealTimeWarehouseInventory request
    // As we have the warehouses data on inventory.additionalResults.["qtyPerWarehouse"] coming in props.qtyPerWarehouse
    // And getRealTimeWarehouseInventory will return inventory isn't available
    const viewAvailabilityByWarehouseClickHandler = () => {
        // setIsLoading(true);
        setModalIsOpen(true);
        // getRealTimeWarehouseInventory({
        //     productId,
        //     unitOfMeasure,
        //     expand: ["warehouses"],
        //     onComplete: result => {
        //         setWarehouses(result.warehouses);
        //         setErrorMessage(result.errorMessage);
        //         setIsLoading(false);
        //     },
        // });
    };

    // XNG-Change
    useEffect(() => {
        if (isProductDetailsPage && qtyPerWarehouse && !warehouses) {
            const qtyPerWarehouseReuslts = qtyPerWarehouse && JSON.parse(qtyPerWarehouse);
            const warehouses: [string, string][] = [];
            qtyPerWarehouseReuslts &&
                Object.keys(qtyPerWarehouseReuslts).forEach((key: string) => {
                    if (qtyPerWarehouseReuslts[key] && qtyPerWarehouseReuslts[key][0] !== "") {
                        warehouses.push(qtyPerWarehouseReuslts[key]);
                    }
                });
            setProductInventoryWarehouses(warehouses);
        }
    }, [qtyPerWarehouse]);

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <InventoryMessage color="danger" {...styles.inventoryMessage}>
                <Icon color="danger" src={Slash} size={12} />
                <Typography {...styles.messageText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </InventoryMessage>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon color={color} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {/* xng-change */}
                    {isAuthenticated ? (
                        parse(availability.message, xParserOptions)
                    ) : (
                        <>
                            {availability.messageType === 1 && <>{translate("In Stock")}</>}
                            {availability.messageType !== 1 &&
                                !parseBoolean(`${steinerAvailabilityDetailsUnauthenticated}`) &&
                                translate("Call for Availability")}
                            {availability.messageType !== 1 &&
                                parseBoolean(`${steinerAvailabilityDetailsUnauthenticated}`) &&
                                siteMessage("STR_NoAvailability_Unauthenticated")}
                        </>
                    )}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
            {/* XNG-Changes */}
            {showLink(
                availability,
                trackInventory,
                productSettings,
                isProductDetailsPage,
                isAuthenticated,
                productInventoryWarehouses,
            ) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {/* XNG-Change */}
                        {/* Change the text to "See By Branch" */}
                        {translate("See By Branch")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <>
                                        <Typography {...styles.availabilityByWarehouseMessage}>
                                            {siteMessage("STR_availability_by_branch_message")}
                                        </Typography>
                                        {productInventoryWarehouses && productInventoryWarehouses.length > 0 && (
                                            <DataTable
                                                {...styles.availabilityByWarehouseTable}
                                                data-test-selector="availabilityByWarehouseModal"
                                                useMobileLayout={false}
                                                usePrintLayout={false}
                                            >
                                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Warehouse")}
                                                    </DataTableHeader>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("QTY")}
                                                    </DataTableHeader>
                                                </DataTableHead>
                                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                    {/* XNG-Changes */}
                                                    {/* read from productInventoryWarehouses instead of warehouses */}
                                                    {productInventoryWarehouses?.map(warehouse => (
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse[0]}
                                                        >
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                            >
                                                                {warehouse?.[0]}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                            >
                                                                {warehouse?.[1]} {unitOfMeasure}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ))}
                                                </DataTableBody>
                                            </DataTable>
                                        )}
                                        <Notify type="info" {...styles.notifyMessage}>
                                            {siteMessage("STR_total_available")} : {qtyOnHand}
                                        </Notify>
                                    </>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
            {/* XNG-Changes: added substitute link here to match classic layout */}
            {isProductDetailsPage && (
                <GridItem {...styles.messageGridItem}>
                    <SubstituteProductsLink />
                </GridItem>
            )}
            {isNonStockPropertyItem && isProductDetailsPage && (
                <GridItem {...styles.messageGridItem}>
                    <Tooltip
                        text={siteMessage("STR_PDP_Item_Special_Terms") as string}
                        triggerComponent={
                            <Link {...styles.nonStockTooltip}>{translate("NONSTOCK. Special terms apply.")}</Link>
                        }
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
